import React, { useState } from 'react';
import { MapPin, Compass, Briefcase, Home as HomeIcon, Crown, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { SwoopyWordmark } from '../components/SwoopyLogo';
import { useUser } from '../context/UserContext';
import { useCurrency } from '../context/CurrencyContext';
import CurrencySelector from '../components/CurrencySelector';
import PlanStatusBadge from '../components/PlanStatusBadge';
import UpgradeModal from '../components/UpgradeModal';

// Animation variants - Clay-inspired with soft bounce
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.15
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 24, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { 
      type: 'spring', 
      stiffness: 260, 
      damping: 20 
    }
  }
};

const buttonVariants = {
  tap: { scale: 0.96 },
  hover: { scale: 1.02, y: -2 }
};

const chipVariants = {
  tap: { scale: 0.96 },
  hover: { y: -3 }
};

export default function HomePage({ onStartPlanning, onSurpriseMe }) {
  const { userStatus, upgradeToBasic, upgradeToPro } = useUser();
  const { formatPrice, getBudgetPresets, BUDGET_PRESETS_IDR } = useCurrency();
  
  // Budget in IDR (default 10 million IDR)
  const [budgetIDR, setBudgetIDR] = useState(10000000);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [activeNav, setActiveNav] = useState('home');

  // Budget presets in IDR
  const BUDGET_CHIPS = [
    { label: 'Rp5jt', value: 5000000 },
    { label: 'Rp10jt', value: 10000000 },
    { label: 'Rp15jt', value: 15000000 },
    { label: 'Rp20jt', value: 20000000 },
  ];

  const handleUpgrade = async (planId) => {
    let result;
    if (planId === 'basic') {
      result = await upgradeToBasic();
    } else if (planId === 'pro') {
      result = await upgradeToPro();
    }
    if (result) {
      setShowUpgradeModal(false);
    }
  };

  const handleChipClick = (chip) => {
    setShowCustomInput(false);
    setBudgetIDR(chip.value);
  };

  const handleSliderChange = (e) => {
    setBudgetIDR(parseInt(e.target.value));
    setShowCustomInput(false);
  };

  const handleCustomSubmit = () => {
    const val = parseInt(customValue);
    // Allow values from 1 million to 50 million IDR
    if (val && val >= 1000000 && val <= 50000000) {
      setBudgetIDR(val);
      setShowCustomInput(false);
      setCustomValue('');
    }
  };

  const handleSurpriseMe = () => {
    onSurpriseMe(budgetIDR);
  };

  // Slider progress (5 million to 25 million IDR range)
  const sliderProgress = ((budgetIDR - 5000000) / 20000000) * 100;

  // Format budget for display
  const formatBudgetDisplay = (amount) => {
    if (amount >= 1000000) {
      return `Rp${(amount / 1000000).toLocaleString('id-ID')}jt`;
    }
    return `Rp${amount.toLocaleString('id-ID')}`;
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--clay-white)', paddingBottom: '100px' }}>
      {/* Sticky Header with Upgrade Button */}
      <motion.header
        className="sticky top-0 z-50 px-5 py-3"
        style={{
          backgroundColor: 'rgba(248, 248, 248, 0.92)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderBottom: '1px solid var(--border-light)'
        }}
        initial={{ y: -60 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 25 }}
      >
        <div className="max-w-md mx-auto flex items-center justify-between">
          {/* Logo - Just the word "Swoopy" */}
          <SwoopyWordmark size="md" />
          
          <div className="flex items-center gap-2">
            {/* Currency Selector */}
            <CurrencySelector compact />
            
            {/* Upgrade Button */}
            <motion.button
              onClick={() => setShowUpgradeModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold"
              style={{
                background: 'linear-gradient(145deg, var(--swoopy-pink-light) 0%, var(--swoopy-pink) 50%, var(--swoopy-pink-dark) 100%)',
                color: 'white',
                boxShadow: 'var(--shadow-clay-btn)'
              }}
              whileTap={{ scale: 0.95 }}
              whileHover={{ scale: 1.02, y: -1 }}
              data-testid="header-upgrade-btn"
            >
              <Crown size={16} />
              <span>Upgrade</span>
            </motion.button>
          </div>
        </div>
      </motion.header>

      <motion.div 
        className="swoopy-container pt-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Hero Section - Just the word "Swoopy" centered */}
        <motion.div className="swoopy-hero pt-8 pb-6" variants={itemVariants}>
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex justify-center"
          >
            <SwoopyWordmark size="xl" />
          </motion.div>
          <p 
            className="mt-5 text-[13px] tracking-widest"
            style={{ color: 'var(--text-muted)', letterSpacing: '0.15em', fontWeight: '400' }}
          >
            Swoop. Sip. Go.
          </p>
        </motion.div>

        {/* Budget Card Section - Clay Style */}
        <motion.section className="mt-4 mb-10" variants={itemVariants}>
          <div className="swoopy-budget-card">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="flex items-center justify-center gap-2 mb-4">
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                >
                  <Sparkles size={18} style={{ color: 'var(--swoopy-pink)' }} />
                </motion.div>
                <span className="swoopy-budget-label">Budget perjalanan</span>
              </div>
              <motion.div 
                className="swoopy-budget-amount"
                key={budgetIDR}
                initial={{ scale: 0.9, opacity: 0.5 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 350, damping: 18 }}
              >
                {formatBudgetDisplay(budgetIDR)}
              </motion.div>
            </div>

            {/* Slider */}
            <div className="px-1 mb-7">
              <input
                type="range"
                min="5000000"
                max="25000000"
                step="1000000"
                value={budgetIDR}
                onChange={handleSliderChange}
                className="swoopy-slider w-full"
                style={{
                  background: `linear-gradient(to right, var(--swoopy-pink) 0%, var(--swoopy-pink) ${sliderProgress}%, var(--border-soft) ${sliderProgress}%, var(--border-soft) 100%)`,
                }}
                data-testid="surprise-slider"
              />
              <div className="flex justify-between text-xs font-bold mt-2" style={{ color: 'var(--text-muted)' }}>
                <span>Rp5jt</span>
                <span>Rp25jt</span>
              </div>
            </div>

            {/* Quick Budget Chips */}
            <div className="flex justify-center gap-3 mb-7 flex-wrap">
              {BUDGET_CHIPS.map((chip) => (
                <motion.button
                  key={chip.label}
                  onClick={() => handleChipClick(chip)}
                  className={`swoopy-chip ${chip.value === budgetIDR ? 'active' : ''}`}
                  variants={chipVariants}
                  whileTap="tap"
                  whileHover="hover"
                  data-testid={`surprise-chip-${chip.value}`}
                >
                  {chip.label}
                </motion.button>
              ))}
              <motion.button
                onClick={() => setShowCustomInput(!showCustomInput)}
                className={`swoopy-chip ${showCustomInput ? 'active' : ''}`}
                variants={chipVariants}
                whileTap="tap"
                whileHover="hover"
                data-testid="surprise-chip-custom"
              >
                Custom
              </motion.button>
            </div>

            {/* Custom Input */}
            {showCustomInput && (
              <motion.div 
                className="flex gap-3 mb-7"
                initial={{ opacity: 0, height: 0, y: -10 }}
                animate={{ opacity: 1, height: 'auto', y: 0 }}
                exit={{ opacity: 0, height: 0, y: -10 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              >
                <div className="flex-1 relative">
                  <span 
                    className="absolute left-5 top-1/2 -translate-y-1/2 text-lg font-bold"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    $
                  </span>
                  <input
                    type="number"
                    placeholder="300 – 1500"
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCustomSubmit()}
                    className="swoopy-input pl-10"
                    autoFocus
                    data-testid="surprise-custom-input"
                  />
                </div>
                <motion.button
                  onClick={handleCustomSubmit}
                  className="swoopy-btn h-[54px] px-8"
                  variants={buttonVariants}
                  whileTap="tap"
                  data-testid="surprise-custom-set"
                >
                  Set
                </motion.button>
              </motion.div>
            )}

            {/* Primary CTA - Clay Button */}
            <motion.button
              onClick={handleSurpriseMe}
              className="swoopy-btn w-full h-[56px] text-[17px]"
              variants={buttonVariants}
              whileTap="tap"
              whileHover="hover"
              data-testid="surprise-me-btn"
            >
              <Sparkles size={20} className="mr-2.5" />
              Surprise me
            </motion.button>
          </div>

          {/* Microcopy */}
          <p 
            className="text-center text-[14px] mt-6 font-medium"
            style={{ color: 'var(--text-muted)' }}
          >
            We&apos;ll pick something you&apos;ll like.
          </p>
        </motion.section>

        {/* Divider */}
        <motion.div className="swoopy-divider mb-8" variants={itemVariants}>
          or search a destination
        </motion.div>

        {/* Search by Destination */}
        <motion.section className="mb-8" variants={itemVariants}>
          <motion.div 
            className="swoopy-search"
            onClick={() => onStartPlanning('destination')}
            whileHover={{ y: -2, boxShadow: 'var(--shadow-clay-lg)' }}
            whileTap={{ scale: 0.99 }}
            data-testid="search-input"
          >
            <MapPin className="icon" size={22} />
            <input 
              type="text" 
              placeholder="Search by destination"
              readOnly
              style={{ cursor: 'pointer' }}
            />
          </motion.div>
        </motion.section>

        {/* Quick Action Tiles */}
        <motion.section className="mb-8" variants={itemVariants}>
          <div className="grid grid-cols-3 gap-4">
            {[
              { icon: MapPin, label: 'Plan', testId: 'plan-tile', action: () => onStartPlanning('destination') },
              { icon: Briefcase, label: 'Trips', testId: 'trips-tile', action: () => {} },
              { icon: Compass, label: 'Explore', testId: 'explore-tile', action: () => {} }
            ].map((tile, index) => (
              <motion.div 
                key={tile.label}
                className="swoopy-tile"
                onClick={tile.action}
                data-testid={tile.testId}
                variants={buttonVariants}
                whileTap="tap"
                whileHover="hover"
                custom={index}
              >
                <div className="tile-icon">
                  <tile.icon size={24} strokeWidth={2.5} />
                </div>
                <span className="tile-label">{tile.label}</span>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Plan Status - Show current tier */}
        {userStatus && (
          <motion.section className="mb-6" variants={itemVariants}>
            <div className="flex justify-center">
              <PlanStatusBadge 
                userStatus={userStatus}
                onClick={() => setShowUpgradeModal(true)}
              />
            </div>
          </motion.section>
        )}
      </motion.div>

      {/* Bottom Navigation - Clay Style */}
      <nav className="swoopy-nav">
        <div className="swoopy-nav-inner">
          <motion.div 
            className={`swoopy-nav-item ${activeNav === 'home' ? 'active' : ''}`}
            onClick={() => setActiveNav('home')}
            whileTap={{ scale: 0.95 }}
          >
            <HomeIcon size={22} strokeWidth={2.5} />
            <span className="nav-label">Home</span>
          </motion.div>
          <motion.div 
            className={`swoopy-nav-item ${activeNav === 'trips' ? 'active' : ''}`}
            onClick={() => setActiveNav('trips')}
            whileTap={{ scale: 0.95 }}
          >
            <Briefcase size={22} strokeWidth={2.5} />
            <span className="nav-label">Trips</span>
          </motion.div>
          <motion.div 
            className={`swoopy-nav-item ${activeNav === 'explore' ? 'active' : ''}`}
            onClick={() => setActiveNav('explore')}
            whileTap={{ scale: 0.95 }}
          >
            <Compass size={22} strokeWidth={2.5} />
            <span className="nav-label">Explore</span>
          </motion.div>
          <motion.div 
            className={`swoopy-nav-item ${activeNav === 'premium' ? 'active' : ''}`}
            onClick={() => { setActiveNav('premium'); setShowUpgradeModal(true); }}
            whileTap={{ scale: 0.95 }}
          >
            <Crown size={22} strokeWidth={2.5} />
            <span className="nav-label">Premium</span>
          </motion.div>
        </div>
      </nav>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={handleUpgrade}
        lockedCount={0}
        planType={userStatus?.planType}
      />
    </div>
  );
}
